import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { BadgeProgressRing } from '../components/BadgeProgressRing';

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ImageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const HELPFUL_SITES = [
  { name: 'TCGdex', url: 'https://tcgdex.dev' },
  { name: 'PkmnCards', url: 'https://pkmncards.com' },
  { name: 'Bulbapedia', url: 'https://bulbapedia.bulbagarden.net' },
  { name: 'Pokemon.com', url: 'https://www.pokemon.com/us/pokemon-tcg/pokemon-cards' },
];

export default function CardResearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { theme } = useTheme();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1); // 1 = upload image, 2 = card details, 3 = done
  const [digitalImage, setDigitalImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Card detail fields
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [setName, setSetName] = useState('');
  const [variant, setVariant] = useState('Normal');
  const [language, setLanguage] = useState('en');

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setDigitalImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!cardName.trim() || !cardNumber.trim() || !setName.trim()) {
      setError('Please fill out card name, number, and set.');
      return;
    }
    if (!digitalImage) {
      setError('Please upload a digital image.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Upload digital image to storage
      let imageUrl = digitalImage;
      if (!digitalImage.startsWith('http')) {
        const blob = await fetch(digitalImage).then(r => r.blob());
        const filename = `research/${user.id}/${Date.now()}-digital.jpg`;
        const { error: uploadError } = await supabase.storage
          .from('Pokemon Card Images')
          .upload(filename, blob, { contentType: 'image/jpeg' });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('Pokemon Card Images')
          .getPublicUrl(filename);
        imageUrl = urlData?.publicUrl;
      }

      // Insert submission
      const { error: insertError } = await supabase
        .from('ai_card_submissions')
        .insert({
          user_id: user.id,
          name: cardName.trim(),
          name_en: cardName.trim(),
          set_name: setName.trim(),
          number: cardNumber.trim(),
          language,
          image_url: imageUrl,
          submission_type: 'community',
          submission_status: 'pending',
          verified: false,
        });

      if (insertError) throw insertError;

      setStep(3);
    } catch (err) {
      console.error('[CardResearch] Submit error:', err);
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: theme.bgInput,
    border: `1px solid ${theme.border}`,
    borderRadius: 10,
    color: theme.text,
    fontSize: 14,
    outline: 'none',
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: theme.textSecondary,
    marginBottom: 4,
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.bg,
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 'env(safe-area-inset-top)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '16px 20px',
        borderBottom: `1px solid ${theme.border}`,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', padding: 4 }}
        >
          <BackIcon />
        </button>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: theme.text, margin: 0 }}>
          Contribute a Card
        </h1>
      </div>

      <div style={{ flex: 1, padding: '20px', maxWidth: 480, margin: '0 auto', width: '100%' }}>
        <AnimatePresence mode="wait">

          {/* ========== STEP 1: Upload Image ========== */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {/* Researcher badge callout */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '14px 16px',
                backgroundColor: 'rgba(14, 165, 233, 0.1)',
                borderRadius: 12,
                marginBottom: 20,
              }}>
                <BadgeProgressRing
                  type="researcher"
                  data={{ imageContributions: activeProfile?.image_contributions || 0 }}
                  size={36}
                  showLabel={false}
                  theme={theme}
                />
                <div>
                  <span style={{ color: theme.text, fontSize: 13, fontWeight: 600, display: 'block' }}>
                    Earn the Researcher Badge
                  </span>
                  <span style={{ color: theme.textSecondary, fontSize: 11 }}>
                    Every approved card levels you up
                  </span>
                </div>
              </div>

              <h2 style={{ fontSize: 16, fontWeight: 700, color: theme.text, margin: '0 0 8px' }}>
                Step 1: Upload a Digital Card Image
              </h2>
              <p style={{ fontSize: 13, color: theme.textSecondary, lineHeight: 1.5, margin: '0 0 20px' }}>
                Find a clean, high-quality digital image of the card (not a photo of a physical card). Try these sites:
              </p>

              {/* Helpful sites */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                {HELPFUL_SITES.map((site) => (
                  <a
                    key={site.name}
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '8px 14px',
                      backgroundColor: theme.bgCard,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                      color: theme.accent,
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {site.name}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                ))}
              </div>

              {/* Upload area */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImagePick}
                style={{ display: 'none' }}
              />

              {digitalImage ? (
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <img
                    src={digitalImage}
                    alt="Card preview"
                    style={{
                      maxWidth: '60%',
                      maxHeight: 300,
                      borderRadius: 12,
                      border: `2px solid ${theme.border}`,
                    }}
                  />
                  <div style={{ marginTop: 10 }}>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: theme.accent,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Change Image
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%',
                    padding: '40px 20px',
                    backgroundColor: theme.bgCard,
                    border: `2px dashed ${theme.border}`,
                    borderRadius: 16,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 20,
                  }}
                >
                  <ImageIcon />
                  <span style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 500 }}>
                    Tap to upload digital image
                  </span>
                  <span style={{ color: theme.textDisabled, fontSize: 11 }}>
                    PNG, JPG, or WebP
                  </span>
                </button>
              )}

              <button
                onClick={() => {
                  if (!digitalImage) {
                    setError('Please upload an image first.');
                    return;
                  }
                  setError(null);
                  setStep(2);
                }}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  backgroundColor: digitalImage ? theme.accent : theme.bgCard,
                  color: digitalImage ? '#fff' : theme.textDisabled,
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: digitalImage ? 'pointer' : 'default',
                }}
              >
                Next: Card Details
              </button>

              {error && (
                <p style={{ color: theme.error, fontSize: 13, marginTop: 10, textAlign: 'center' }}>{error}</p>
              )}
            </motion.div>
          )}

          {/* ========== STEP 2: Card Details ========== */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <h2 style={{ fontSize: 16, fontWeight: 700, color: theme.text, margin: '0 0 4px' }}>
                Step 2: Card Details
              </h2>
              <p style={{ fontSize: 13, color: theme.textSecondary, margin: '0 0 20px' }}>
                Enter the card info exactly as it appears on the card.
              </p>

              {/* Preview thumbnail */}
              {digitalImage && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                  <img
                    src={digitalImage}
                    alt="Card"
                    style={{
                      width: 80,
                      height: 'auto',
                      borderRadius: 8,
                      border: `1px solid ${theme.border}`,
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Card Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Charizard ex"
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Card Number *</label>
                    <input
                      type="text"
                      placeholder="e.g. 025/172"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Variant</label>
                    <select
                      value={variant}
                      onChange={(e) => setVariant(e.target.value)}
                      style={inputStyle}
                    >
                      <option value="Normal">Normal</option>
                      <option value="Holo">Holo</option>
                      <option value="Reverse Holo">Reverse Holo</option>
                      <option value="Full Art">Full Art</option>
                      <option value="Alt Art">Alt Art</option>
                      <option value="Special Art">Special Art</option>
                      <option value="Gold">Gold</option>
                      <option value="Promo">Promo</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Set Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Obsidian Flames"
                    value={setName}
                    onChange={(e) => setSetName(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Language</label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    style={inputStyle}
                  >
                    <option value="en">English</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                    <option value="zh-tw">Chinese (Traditional)</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                    <option value="es">Spanish</option>
                    <option value="pt">Portuguese</option>
                  </select>
                </div>
              </div>

              {error && (
                <p style={{ color: theme.error, fontSize: 13, marginTop: 10, textAlign: 'center' }}>{error}</p>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <button
                  onClick={() => { setError(null); setStep(1); }}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.border}`,
                    borderRadius: 12,
                    color: theme.text,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{
                    flex: 2,
                    padding: '14px 20px',
                    backgroundColor: theme.accent,
                    border: 'none',
                    borderRadius: 12,
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: isSubmitting ? 'default' : 'pointer',
                    opacity: isSubmitting ? 0.6 : 1,
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit for Review'}
                </button>
              </div>
            </motion.div>
          )}

          {/* ========== STEP 3: Success ========== */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              style={{ textAlign: 'center', paddingTop: 40 }}
            >
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>

              <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.text, margin: '0 0 8px' }}>
                Submitted!
              </h2>
              <p style={{ fontSize: 14, color: theme.textSecondary, lineHeight: 1.5, margin: '0 0 24px' }}>
                Your card is pending review. When approved, your Researcher badge will level up!
              </p>

              {/* Researcher badge */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                padding: '12px 20px',
                backgroundColor: 'rgba(14, 165, 233, 0.1)',
                borderRadius: 12,
                marginBottom: 24,
              }}>
                <BadgeProgressRing
                  type="researcher"
                  data={{ imageContributions: activeProfile?.image_contributions || 0 }}
                  size={32}
                  showLabel={false}
                  theme={theme}
                />
                <span style={{ color: theme.accent, fontSize: 13, fontWeight: 600 }}>
                  +1 when approved
                </span>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => {
                    setDigitalImage(null);
                    setCardName('');
                    setCardNumber('');
                    setSetName('');
                    setVariant('Normal');
                    setLanguage('en');
                    setStep(1);
                  }}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.border}`,
                    borderRadius: 12,
                    color: theme.text,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Add Another
                </button>
                <button
                  onClick={() => navigate(-1)}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    backgroundColor: theme.accent,
                    border: 'none',
                    borderRadius: 12,
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}
